<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <!-- Title Bar -->
      <div class="title-bar">
        <h2 class="modal-title">{{ modalTitle }}</h2>
        <button class="close-button" @click="closeModal">
          <img src="@/assets/close-icon.svg" alt="close" />
        </button>
      </div>

      <!-- Form Content -->
      <div class="form-content">
        <!-- Loading State -->
        <div v-if="isLoadingCompanyId" class="loading-container">
          <div class="loading-spinner"></div>
          <span class="loading-text">Verificando información del grupo...</span>
        </div>

        <!-- Alert Message: No Company ID -->
        <div v-if="!isLoadingCompanyId && !hasCompanyId" class="alert-message">
          <span class="alert-text">
            El grupo actual no cuenta con <strong>ID de Empresa</strong>, es necesario asignarlo en
            <strong>CORE</strong> para continuar
          </span>
        </div>

        <!-- Billing Month Selection (only for new register) -->
        <div v-if="isNewRegister && !isLoadingCompanyId" class="input-field date-field">
          <label class="input-title">Mes de facturación correspondiente*</label>
          <div class="input-box">
            <CustomDatePicker
              v-model="formData.billingMonth"
              picker-type="month-year"
              placeholder="------ de ----"
              :has-error="errors.billingMonth"
              :readonly="isBillingPickerDisabled || !hasCompanyId"
              :required="true"
              :allow-future-months="false"
              teleport=".modal-container"
              error-message="Es necesario seleccionar el mes de facturación para continuar"
              class="modal-date-picker billing-month-picker"
              @focus="
                () => {
                  if (hasCompanyId) {
                    clearError('billingMonth')
                  }
                }
              "
            />
          </div>
        </div>

        <!-- Account Statement Upload -->
        <div v-if="!isLoadingCompanyId" class="input-field file-field">
          <div class="input-box">
            <CustomInputFileSelector
              v-model="formData.accountStatement"
              label="Cargar Estado de Cuenta*"
              :placeholder="FILE_UPLOAD_MESSAGES.SELECT_FILE_PLACEHOLDER"
              :accept="ALLOWED_FILE_EXTENSIONS_ACCEPT"
              :required="true"
              :has-error="errors.accountStatement"
              :disabled="!hasCompanyId"
              :error-message="FILE_UPLOAD_MESSAGES.FILE_REQUIRED"
              :allowed-extensions="ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE"
              :max-size="MAX_FILE_SIZE_MB"
              :current-file-name="props.currentAccountStatementName"
              @focus="clearError('accountStatement')"
              @error="handleFileError"
            />
          </div>
        </div>

        <!-- Pre-invoice Upload -->
        <div v-if="!isLoadingCompanyId" class="input-field file-field">
          <div class="input-box">
            <CustomInputFileSelector
              v-model="formData.preInvoice"
              label="Cargar Pre-factura*"
              :placeholder="FILE_UPLOAD_MESSAGES.SELECT_FILE_PLACEHOLDER"
              :accept="ALLOWED_FILE_EXTENSIONS_ACCEPT"
              :required="true"
              :has-error="isNewRegister && errors.preInvoice"
              :disabled="!hasCompanyId"
              :error-message="FILE_UPLOAD_MESSAGES.FILE_REQUIRED"
              :allowed-extensions="ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE"
              :max-size="MAX_FILE_SIZE_MB"
              :current-file-name="props.currentPreInvoiceName"
              @focus="clearError('preInvoice')"
              @error="handleFileError"
            />
          </div>
        </div>

        <!-- Pre-invoice number -->
        <div v-if="!isLoadingCompanyId" class="input-field">
          <CustomInput
            id="invoice-number"
            v-model="formData.invoiceNumber"
            :label="isNewRegister ? 'Número de Pre-factura*' : 'Número de Factura*'"
            type="text"
            :placeholder="
              isNewRegister ? 'Ingresa el número de pre-factura' : 'Ingresa el número de factura'
            "
            :show-error="errors.invoiceNumber"
            :disabled="!hasCompanyId"
            :error-message="
              isNewRegister
                ? 'Es necesario ingresar el número de Pre-factura'
                : 'Es necesario ingresar el número de Factura'
            "
            @focus="clearError('invoiceNumber')"
            @blur="validateInvoiceNumber"
          />
          <div v-if="errors.invoiceNumber" class="error-message">
            {{
              isNewRegister
                ? 'Es necesario ingresar el número de Pre-factura'
                : 'Es necesario ingresar el número de Factura'
            }}
          </div>
        </div>

        <!-- Amount to Pay -->
        <div v-if="!isLoadingCompanyId" class="input-field">
          <CustomInput
            id="amount-to-pay"
            v-model="formData.amountToPay"
            label="Monto a pagar*"
            type="text"
            :placeholder="ZERO_PLACEHOLDER"
            :show-error="errors.amountToPay"
            error-message="Es necesario ingresar el monto a pagar"
            :money-type="true"
            :disabled="!hasCompanyId"
            @focus="clearError('amountToPay')"
            @blur="validateAmountToPay"
          />
          <div v-if="errors.amountToPay" class="error-message">
            Es necesario ingresar el monto a pagar
          </div>
        </div>

        <!-- Invoice Upload (only for edit mode) -->
        <div
          v-if="!isNewRegister && !isLoadingCompanyId"
          class="input-field file-field invoice-field"
        >
          <label class="input-title">Factura</label>
          <div class="input-box">
            <CustomInputFileSelector
              v-model="formData.invoice"
              label=""
              :placeholder="FILE_UPLOAD_MESSAGES.SELECT_FILE_PLACEHOLDER"
              :accept="ALLOWED_FILE_EXTENSIONS_ACCEPT"
              :required="false"
              :has-error="false"
              :disabled="!hasCompanyId"
              :allowed-extensions="ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE"
              :max-size="MAX_FILE_SIZE_MB"
              :current-file-name="props.currentInvoiceName"
              @error="handleFileError"
            />
          </div>
        </div>

        <!-- XML Invoice Upload (only for edit mode) -->
        <div
          v-if="!isNewRegister && !isLoadingCompanyId"
          class="input-field file-field invoice-field"
        >
          <label class="input-title">Archivo XML</label>
          <div class="input-box">
            <CustomInputFileSelector
              v-model="formData.xmlInvoice"
              label=""
              :placeholder="FILE_UPLOAD_MESSAGES.SELECT_FILE_PLACEHOLDER"
              :accept="ALLOWED_FILE_EXTENSIONS.XML"
              :required="false"
              :has-error="false"
              :disabled="!hasCompanyId"
              :allowed-extensions="[ALLOWED_FILE_EXTENSIONS.XML]"
              :max-size="MAX_FILE_SIZE_MB"
              :current-file-name="props.currentXmlInvoiceName"
              @error="handleFileError"
            />
          </div>
        </div>

        <!-- Payment voucher Upload (only for edit mode) -->
        <div
          v-if="!isNewRegister && !isLoadingCompanyId"
          class="input-field file-field payment-proof-field"
        >
          <label class="input-title">Comprobante de pago</label>
          <div class="input-box">
            <CustomInputFileSelector
              v-model="formData.paymentProof"
              label=""
              :placeholder="FILE_UPLOAD_MESSAGES.SELECT_FILE_PLACEHOLDER"
              :accept="ALLOWED_FILE_EXTENSIONS_ACCEPT"
              :required="false"
              :has-error="false"
              :disabled="!hasCompanyId"
              :allowed-extensions="ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE"
              :max-size="MAX_FILE_SIZE_MB"
              :current-file-name="props.currentPaymentProofName"
              @error="handleFileError"
            />
          </div>
        </div>

        <div v-if="!isNewRegister && !isLoadingCompanyId" class="input-field date-field">
          <label class="input-title">Fecha en que se realizó el pago</label>
          <div class="input-box">
            <CustomDatePicker
              v-model="formData.paymentMadeDate"
              picker-type="date"
              placeholder="dd/mm/aaaa"
              :has-error="errors.paymentMadeDate"
              :readonly="false"
              :required="false"
              :allow-future-months="false"
              teleport=".modal-container"
              class="modal-date-picker billing-month-picker"
              @focus="
                () => {
                  if (hasCompanyId) {
                    clearError('paymentMadeDate')
                    openPicker('billing')
                  }
                }
              "
            />
          </div>
        </div>

        <!-- Send by Email Checkbox -->
        <div v-if="!isLoadingCompanyId" class="checkbox-container">
          <div class="checkbox-wrapper">
            <input
              v-model="formData.sendByEmail"
              type="checkbox"
              :id="checkboxId"
              :disabled="isCheckboxDisabled || !hasCompanyId"
              class="checkbox-input"
            />
            <label :for="checkboxId" class="checkbox-label">
              <span class="checkbox-custom" :class="{ checked: formData.sendByEmail }"></span>
              {{ checkboxText }}
            </label>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="button-container">
        <CustomButton text="Cancelar" variant="cancel" :show-icon="false" @click="closeModal" />
        <CustomButton
          :text="saveButtonText"
          variant="secondary"
          :show-icon="false"
          :disabled="!isFormValid || isLoading || !hasCompanyId"
          @click="handleSave"
        />
      </div>
    </div>

    <!-- Toast Message -->
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :type="toastType"
      :icon="toastIcon"
      :duration="5000"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, inject } from 'vue'
import CustomButton from './CustomButton.vue'
import CustomInput from './CustomInput.vue'
import CustomDatePicker from './CustomDatePicker.vue'
import CustomInputFileSelector from './CustomInputFileSelector.vue'
import ToastMessage from './ToastMessage.vue'
import { FileTypes } from 'commons'
import type { HistoryItem } from '@/utils/file-history'
import { ZERO_PLACEHOLDER } from '../utils/constants'
import { normalizeAmount } from '@/utils/amountUtils'
import errorIcon from '@/assets/error-icon-white.svg'
import { ApiService } from '@/services/apiService'
import { useBulkLoadStore } from '@/stores/bulkLoad'
import {
  ALLOWED_FILE_EXTENSIONS_ACCEPT,
  ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE,
  MAX_FILE_SIZE_MB,
  FILE_UPLOAD_MESSAGES,
  ALLOWED_FILE_EXTENSIONS,
} from '@/utils/file-constants'

interface Props {
  isVisible: boolean
  isNewRegister?: boolean
  documentType?: 'account-statement' | 'pre-invoice'
  billingMonth?: string
  billingYear?: string
  currentPaymentDueDate?: string
  currentFileName?: string
  editingItemData?: HistoryItem | null
  currentAccountStatementName?: string
  currentPreInvoiceName?: string
  currentInvoiceName?: string
  currentPaymentProofName?: string
  isLoading?: boolean
  currentPaymentMadeDate?: string
  currentXmlInvoiceName?: string
}

const props = withDefaults(defineProps<Props>(), {
  isNewRegister: true,
  documentType: 'account-statement',
  billingMonth: '',
  billingYear: '',
  currentPaymentDueDate: '',
  currentFileName: '',
  editingItemData: null,
  currentAccountStatementName: '',
  currentPreInvoiceName: '',
  currentInvoiceName: '',
  currentPaymentProofName: '',
  isLoading: false,
  currentPaymentMadeDate: '',
  currentXmlInvoiceName: '',
})

const emit = defineEmits<{
  close: []
  save: [
    data: {
      paymentDueDate: string
      files: { file: File; fileType: FileTypes }[]
      billingMonth?: string
      invoiceNumber?: string
      amountToPay?: string
      concept?: string
      paymentMadeDate?: string
      sendByEmail?: boolean
      idEmpresa?: string | number | null
      idEmpresaFromGetGroups?: boolean
    },
  ]
}>()

// Form data
const formData = reactive({
  billingMonth: '',
  invoiceNumber: '',
  paymentDueDate: '',
  accountStatement: null as File | null,
  preInvoice: null as File | null,
  amountToPay: '',
  concept: '',
  paymentExtensionDate: '',
  paymentMadeDate: '',
  invoice: null as File | null,
  paymentProof: null as File | null,
  sendByEmail: false,
  xmlInvoice: null as File | null,
})

// Form errors
const errors = reactive({
  billingMonth: false,
  paymentDueDate: false,
  accountStatement: false,
  preInvoice: false,
  invoiceNumber: false,
  amountToPay: false,
  concept: false,
  paymentMadeDate: false,
})

// Toast state
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('error')
const toastIcon = ref(errorIcon)

// API service and store
const apiService = inject<ApiService>('apiService')
const bulkLoadStore = useBulkLoadStore()

if (!apiService) {
  throw new Error('apiService is not provided')
}

// Company ID check state
const hasCompanyId = ref(true)
const isLoadingCompanyId = ref(false)
const idEmpresa = ref<string | number | null>(null)
const idEmpresaFromGetGroups = ref(false) // Flag to track if idEmpresa came from getGroups API

// Active picker state management
const activePicker = ref<null | 'billing' | 'paymentDue' | 'extension' | 'paymentMade'>(null)

// Computed properties for disabled states
const isBillingPickerDisabled = computed(
  () => activePicker.value !== null && activePicker.value !== 'billing',
)

const modalTitle = computed(() => {
  if (props.isNewRegister) {
    return 'Nuevo mes de facturación'
  } else {
    return `Editar ${props.billingMonth?.toUpperCase()} ${props.billingYear}`
  }
})

const checkboxText = computed(() => {
  return props.isNewRegister ? 'Enviar notificación por correo' : 'Enviar notificación por correo'
})

const isCheckboxDisabled = computed(() => {
  if (props.isNewRegister) {
    return false
  } else {
    return !formData.invoice
  }
})

const saveButtonText = computed(() => {
  return props.isNewRegister ? 'Generar nuevo mes' : 'Guardar cambios'
})

const checkboxId = computed(() => {
  return props.isNewRegister ? 'send-documents-email' : 'send-invoice-email'
})

const isNewRegisterValid = computed(() => {
  return (
    formData.billingMonth.trim() !== '' &&
    (formData.accountStatement !== null || props.currentAccountStatementName !== '') &&
    (formData.preInvoice !== null || props.currentPreInvoiceName !== '') &&
    formData.invoiceNumber.trim() !== '' &&
    formData.amountToPay.trim() !== '' &&
    normalizeAmount(formData.amountToPay) !== '0'
  )
})

const isEditModeValid = computed(() => {
  if (!props.editingItemData) return false

  const originalAmountToPay = props.editingItemData.amountToPay
  const currentAmountToPay = formData.amountToPay
  const normalizedOriginalAmount = normalizeAmount(originalAmountToPay)
  const normalizedCurrentAmount = normalizeAmount(currentAmountToPay)

  return (
    formData.accountStatement !== null ||
    formData.preInvoice !== null ||
    formData.invoice !== null ||
    formData.paymentProof !== null ||
    formData.paymentDueDate !== (props.editingItemData.paymentLimitDate || '') ||
    normalizedCurrentAmount !== normalizedOriginalAmount ||
    formData.concept !== (props.editingItemData.concept || '') ||
    formData.invoiceNumber !== (props.editingItemData.invoiceNumber || '') ||
    formData.paymentMadeDate !== (props.editingItemData.paymentMadeDate || '') ||
    formData.xmlInvoice !== null
  )
})

const isFormValid = computed(() => {
  return props.isNewRegister ? isNewRegisterValid.value : isEditModeValid.value
})

// Methods
const clearError = (field: keyof typeof errors) => {
  if (field === 'accountStatement') {
    // Clear error if a new file is selected OR an existing file name is present
    if (formData.accountStatement || props.currentAccountStatementName) {
      errors.accountStatement = false
    }
  } else if (field === 'preInvoice') {
    // Clear error if a new file is selected OR an existing file name is present
    if (formData.preInvoice || props.currentPreInvoiceName) {
      errors.preInvoice = false
    }
  } else if (field === 'invoiceNumber') {
    // For invoiceNumber, watchers handle real-time validation
    // Only clear if value is valid
    if (formData.invoiceNumber.trim() !== '') {
      errors.invoiceNumber = false
    }
  } else if (field === 'amountToPay') {
    // For amountToPay, watchers handle real-time validation
    // Only clear if value is valid
    const normalized = normalizeAmount(formData.amountToPay)
    if (formData.amountToPay.trim() !== '' && normalized !== '0') {
      errors.amountToPay = false
    }
  } else {
    errors[field] = false
  }
}

const openPicker = (picker: typeof activePicker.value) => {
  activePicker.value = picker
}

const validateInvoiceNumber = () => {
  // This is called on blur, but watchers handle real-time validation
  // Only validate if in new register mode
  if (props.isNewRegister) {
    if (!formData.invoiceNumber.trim()) {
      errors.invoiceNumber = true
    } else {
      errors.invoiceNumber = false
    }
  }
}

const validateAmountToPay = () => {
  // This is called on blur, but watchers handle real-time validation
  // Only validate if in new register mode
  if (props.isNewRegister) {
    if (!formData.amountToPay.trim() || normalizeAmount(formData.amountToPay) === '0') {
      errors.amountToPay = true
    } else {
      errors.amountToPay = false
    }
  }
}

// Watch for invoice file changes to handle checkbox state
watch(
  () => formData.invoice,
  (newInvoice) => {
    if (!props.isNewRegister && !newInvoice && formData.sendByEmail) {
      // If invoice is removed and checkbox was checked, uncheck it
      formData.sendByEmail = false
    }
  },
)

// Watch invoiceNumber for real-time validation
watch(
  () => formData.invoiceNumber,
  (newValue) => {
    if (props.isNewRegister) {
      if (newValue.trim() !== '') {
        errors.invoiceNumber = false
      } else {
        errors.invoiceNumber = true
      }
    }
  },
)

// Watch amountToPay for real-time validation
watch(
  () => formData.amountToPay,
  (newValue) => {
    if (props.isNewRegister) {
      const normalized = normalizeAmount(newValue)
      if (newValue.trim() !== '' && normalized !== '0') {
        errors.amountToPay = false
      } else {
        errors.amountToPay = true
      }
    }
  },
)

// Check if group has company ID
const checkCompanyId = async () => {
  try {
    isLoadingCompanyId.value = true
    const businessGroupId = bulkLoadStore.selectedGroup?.id
    if (!businessGroupId) {
      hasCompanyId.value = false
      return
    }

    // First, check if idEmpresa already exists in selectedGroup
    const storeIdEmpresa = bulkLoadStore.selectedGroup?.idEmpresa
    const hasStoreIdEmpresa =
      storeIdEmpresa !== null &&
      storeIdEmpresa !== undefined &&
      storeIdEmpresa !== '' &&
      String(storeIdEmpresa).trim() !== '' &&
      String(storeIdEmpresa) !== '0'

    if (hasStoreIdEmpresa) {
      // Use idEmpresa from store
      idEmpresa.value = storeIdEmpresa ?? null
      idEmpresaFromGetGroups.value = false
      hasCompanyId.value = true
      return
    }

    // If not in store, fetch from getGroups API
    const groups = await apiService.getGroups()
    // Convert both to string for comparison since id can be number or string
    const currentGroup = groups.find((group) => String(group.id) === String(businessGroupId))

    if (!currentGroup) {
      hasCompanyId.value = false
      idEmpresaFromGetGroups.value = false
      return
    }

    // Check if group has idEmpresa (not null/undefined and not empty string/0)
    // idEmpresa can be string or number, so we check both cases
    const currentIdEmpresa = currentGroup.idEmpresa
    idEmpresa.value = currentIdEmpresa ?? null
    idEmpresaFromGetGroups.value = true // Mark that it came from getGroups

    // Update the store with the idEmpresa for future use
    if (currentIdEmpresa !== null && currentIdEmpresa !== undefined) {
      const updatedGroup = {
        ...bulkLoadStore.selectedGroup,
        idEmpresa: currentIdEmpresa,
      }
      bulkLoadStore.setSelectedGroup(updatedGroup)
    }

    const hasValidIdEmpresa =
      currentIdEmpresa !== null &&
      currentIdEmpresa !== undefined &&
      currentIdEmpresa !== '' &&
      String(currentIdEmpresa).trim() !== '' &&
      String(currentIdEmpresa) !== '0'

    hasCompanyId.value = hasValidIdEmpresa
  } catch (error) {
    console.error('Hubo un error: Error checking company ID ', error)
    // On error, assume it doesn't have company ID to be safe
    hasCompanyId.value = false
    idEmpresaFromGetGroups.value = false
  } finally {
    isLoadingCompanyId.value = false
  }
}

// Watch for visibility changes to reset form and active picker
watch(
  () => props.isVisible,
  async (newValue) => {
    if (newValue) {
      // Reset form data first
      formData.billingMonth = ''
      formData.invoiceNumber = ''
      formData.paymentDueDate = props.currentPaymentDueDate || ''
      formData.accountStatement = null
      formData.preInvoice = null
      formData.amountToPay = ''
      formData.concept = ''
      formData.paymentMadeDate = ''
      formData.invoice = null
      formData.paymentProof = null
      formData.sendByEmail = false
      formData.xmlInvoice = null

      // Reset idEmpresa and flag before checking
      idEmpresa.value = null
      idEmpresaFromGetGroups.value = false

      // Check company ID when modal opens (this will set idEmpresa.value)
      await checkCompanyId()

      // Pre-populate with editing data if available
      if (props.editingItemData && !props.isNewRegister) {
        formData.paymentDueDate = props.editingItemData.paymentLimitDate || ''
        // paymentExtensionDate is calculated in the backend, so we don't send it
        // paymentMadeDate is calculated automatically when invoice is uploaded, so we don't pre-populate it
        formData.invoiceNumber = props.editingItemData.invoiceNumber || ''
        // Handle amountToPay: if it's 0 or empty, show placeholder, otherwise show the value
        const amountValue = props.editingItemData.amountToPay
        formData.amountToPay =
          amountValue && amountValue !== '0' && amountValue !== ZERO_PLACEHOLDER ? amountValue : ''
        formData.concept = props.editingItemData.concept || ''
        formData.paymentMadeDate = props.editingItemData.paymentMadeDate || ''
      }

      // Validate required fields when modal opens (only for new register)
      if (props.isNewRegister) {
        // Validate fields immediately when modal opens
        const fieldValidations = [
          { condition: !formData.billingMonth.trim(), key: 'billingMonth' },
          {
            condition: !formData.accountStatement && !props.currentAccountStatementName,
            key: 'accountStatement',
          },
          { condition: !formData.preInvoice && !props.currentPreInvoiceName, key: 'preInvoice' },
          { condition: !formData.invoiceNumber.trim(), key: 'invoiceNumber' },
          {
            condition:
              !formData.amountToPay.trim() || normalizeAmount(formData.amountToPay) === '0',
            key: 'amountToPay',
          },
        ]
        fieldValidations.forEach(({ condition, key }) => {
          if (condition) {
            errors[key as keyof typeof errors] = true
          }
        })
      } else {
        // Clear errors for edit mode
        Object.keys(errors).forEach((key) => {
          errors[key as keyof typeof errors] = false
        })
      }

      // Reset active picker
      activePicker.value = null
    }
  },
)

const handleFileError = (errorMessage: string) => {
  toastMessage.value = 'Error de validación'
  toastSubmessage.value = errorMessage
  toastType.value = 'error'
  toastIcon.value = errorIcon
  showToast.value = true
}

const closeModal = () => {
  emit('close')
}

/**
 * Validates form fields and sets error states.
 *
 * @returns True if there are validation errors
 */
const validateFormFields = (): boolean => {
  let hasErrors = false

  if (props.isNewRegister) {
    if (!formData.billingMonth.trim()) {
      errors.billingMonth = true
      hasErrors = true
    }
    if (!formData.accountStatement && !props.currentAccountStatementName) {
      errors.accountStatement = true
      hasErrors = true
    }
    if (!formData.preInvoice && !props.currentPreInvoiceName) {
      errors.preInvoice = true
      hasErrors = true
    }
  }

  // Validate invoiceNumber and amountToPay in both modes
  if (!formData.invoiceNumber.trim()) {
    errors.invoiceNumber = true
    hasErrors = true
  }
  if (!formData.amountToPay.trim() || normalizeAmount(formData.amountToPay) === '0') {
    errors.amountToPay = true
    hasErrors = true
  }

  return hasErrors
}

/**
 * Collects all files from form data with their types.
 *
 * @returns Array of files with their types
 */
const collectFiles = (): { file: File; fileType: FileTypes }[] => {
  const files: { file: File; fileType: FileTypes }[] = []

  if (formData.accountStatement) {
    files.push({ file: formData.accountStatement, fileType: FileTypes.ACCOUNT_STATEMENT })
  }

  if (formData.preInvoice) {
    files.push({ file: formData.preInvoice, fileType: FileTypes.PRE_INVOICE })
  }

  if (formData.invoice) {
    files.push({ file: formData.invoice, fileType: FileTypes.INVOICE })
  }

  if (formData.xmlInvoice) {
    files.push({ file: formData.xmlInvoice, fileType: FileTypes.XML_INVOICE })
  }

  if (formData.paymentProof) {
    files.push({ file: formData.paymentProof, fileType: FileTypes.PAYMENT_PROOF })
  }

  console.log('BillingMonthModal - collectFiles:', files)
  return files
}

/**
 * Calculates paymentMadeDate based on form state.
 *
 * @returns The payment made date string or undefined
 */
const calculatePaymentMadeDate = (): string | undefined => {
  if (!props.isNewRegister) {
    // Keep existing paymentMadeDate if no invoice is uploaded
    return formData.paymentMadeDate || undefined
  }
  return undefined
}

/**
 * Creates the save data object to emit.
 *
 * @param files - Array of files with their types
 * @param paymentMadeDate - The payment made date
 * @returns The save data object
 */
const createSaveData = (
  files: { file: File; fileType: FileTypes }[],
  paymentMadeDate: string | undefined,
) => {
  return {
    paymentDueDate: formData.paymentDueDate,
    files: files,
    billingMonth: props.isNewRegister ? formData.billingMonth : undefined,
    invoiceNumber: formData.invoiceNumber,
    amountToPay: formData.amountToPay,
    concept: formData.concept,
    paymentMadeDate: paymentMadeDate,
    sendByEmail: formData.sendByEmail,
    idEmpresa: idEmpresa.value ?? null,
    idEmpresaFromGetGroups: idEmpresaFromGetGroups.value,
  }
}

const handleSave = async () => {
  const hasErrors = validateFormFields()

  if (hasErrors || !isFormValid.value) {
    return
  }

  const files = collectFiles()
  const paymentMadeDate = calculatePaymentMadeDate()

  // Ensure idEmpresa is loaded if not already loaded
  if (idEmpresa.value === null && bulkLoadStore.selectedGroup?.id) {
    await checkCompanyId()
  }

  const saveData = createSaveData(files, paymentMadeDate)

  console.log('BillingMonthModal - saveData.idEmpresa:', saveData.idEmpresa)
  console.log('BillingMonthModal - idEmpresa.value:', idEmpresa.value)
  console.log('BillingMonthModal - idEmpresaFromGetGroups:', idEmpresaFromGetGroups.value)

  emit('save', saveData)
}
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

/* Modal Container */
.modal-container {
  width: 863px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px 0px 16px;
  gap: 8px;
  position: relative;
  overflow: hidden;
  z-index: 10000;
}

/* Title Bar */
.title-bar {
  width: 863px;
  height: 59px;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 0px 0px 16px 16px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
}

.modal-title {
  width: 645px;
  height: 31px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 130%;
  color: #4f4f4f;
  margin: 0;
  text-align: left;
}

.close-button {
  width: 32px;
  height: 32px;
  border-radius: 50px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button img {
  width: 20px;
  height: 20px;
}

/* Form Content */
.form-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px 40px;
  gap: 16px;
  isolation: isolate;
  width: 100%;
  flex: 1;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
  z-index: 1;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: calc(90vh - 120px);
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px 24px;
  gap: 16px;
  width: 100%;
  min-height: 120px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f2f2f2;
  border-top: 4px solid #4a90e2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #4f4f4f;
  text-align: center;
}

/* Alert Message: No Company ID */
.alert-message {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 24px;
  gap: 15px;
  width: 100%;
  max-width: 431px;
  min-height: 76px;
  background: #ffd1cf;
  border: 1px solid #ff6262;
  border-radius: 8px;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 7;
  align-self: center;
  margin: 0 auto;
}

.alert-text {
  width: 100%;
  max-width: 383px;
  min-height: 44px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 0;
  flex-grow: 0;
}

.alert-text strong {
  font-weight: 700;
}

/* Input Field */
.input-field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 1px;
  isolation: isolate;
  width: 100%;
  min-height: 80px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
}

.input-title {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  height: 22px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
}

.input-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 2px;
  width: 100%;
  min-height: 69px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
}

.input-text {
  width: 100%;
  height: 48px;
  background: #f2f2f2;
  border-radius: 16px;
  border: none;
  padding: 0 16px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  box-sizing: border-box;
}

.input-text::placeholder {
  color: #a1a1a1;
}

.helper-text {
  width: 100%;
  height: 17px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 1%;
  color: #000000;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  white-space: nowrap;
}

.error-message {
  width: 100%;
  height: 17px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 130%;
  color: #ff0000;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
}

/* Checkbox Container */
.checkbox-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 279px;
  height: 24px;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox-input {
  display: none;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #000000;
}

.checkbox-custom {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: #f2f2f2;
  border: 2px solid #e0e0e0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkbox-custom.checked {
  background: #9cd41c;
  border-color: #9cd41c;
}

.checkbox-custom.checked::after {
  content: '';
  width: 8px;
  height: 4px;
  border-left: 2px solid #ffffff;
  border-bottom: 2px solid #ffffff;
  transform: rotate(-45deg);
}

.checkbox-input:disabled + .checkbox-label {
  opacity: 0.5;
  cursor: not-allowed;
}

.checkbox-input:disabled + .checkbox-label .checkbox-custom {
  background: #f2f2f2;
  border-color: #e0e0e0;
  cursor: not-allowed;
}

/* Button Container */
.button-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0px 40px;
  gap: 20px;
  width: 863px;
  height: 48px;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
  flex-shrink: 0;
}

.cancel-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 4px;
  width: 240px;
  height: 48px;
  border-radius: 30px;
  background: transparent;
  border: none;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #1a1a1a;
  cursor: pointer;
}

.cancel-button:hover {
  background: #f5f5f5;
}

/* Override date picker z-index when used in modal - use extremely high values for ALL pickers */
:deep(.modal-container .modal-date-picker .date-picker-overlay),
:deep(.modal-container .modal-date-picker .month-year-picker-overlay) {
  z-index: 999999 !important;
}

:deep(.modal-container .modal-date-picker .date-picker),
:deep(.modal-container .modal-date-picker .month-year-picker) {
  z-index: 999999 !important;
}

/* Force file input to have very low z-index */
.file-field {
  z-index: 1 !important;
  position: relative;
}

.file-field * {
  z-index: 1 !important;
}

.date-field {
  z-index: 2 !important;
  position: relative;
}

/* Override CustomInputFileSelector z-index specifically */
:deep(.file-field .custom-input-selector) {
  z-index: 1 !important;
}

:deep(.file-field .custom-input-selector *) {
  z-index: 1 !important;
}

:deep(.date-field .modal-date-picker) {
  z-index: 2 !important;
}

/* Reduce gap for invoice field in edit mode */
.invoice-field {
  gap: 0px !important;
  margin-bottom: 0px !important;
  min-height: auto !important;
}

.invoice-field > .input-title {
  margin-bottom: 0px !important;
  padding-bottom: 0px !important;
  height: auto !important;
  min-height: 0 !important;
  line-height: 1.2 !important;
  margin-top: 0px !important;
}

.invoice-field > .input-box {
  margin-top: 0px !important;
  padding-top: 0px !important;
  min-height: auto !important;
}

/* Override CustomInputFileSelector internal spacing - hide its label since we have our own */
:deep(.invoice-field .input-box .input-field) {
  gap: 0px !important;
  margin-top: 0px !important;
  padding-top: 0px !important;
  min-height: auto !important;
}

:deep(.invoice-field .input-box .input-field .input-title) {
  display: none !important;
}

/* Reduce gap for payment proof field in edit mode */
.payment-proof-field {
  gap: 0px !important;
  margin-bottom: 0px !important;
  min-height: auto !important;
}

.payment-proof-field > .input-title {
  margin-bottom: 0px !important;
  padding-bottom: 0px !important;
  height: auto !important;
  min-height: 0 !important;
  line-height: 1.2 !important;
  margin-top: 0px !important;
}

.payment-proof-field > .input-box {
  margin-top: 0px !important;
  padding-top: 0px !important;
  min-height: auto !important;
}

/* Override CustomInputFileSelector internal spacing - hide its label since we have our own */
:deep(.payment-proof-field .input-box .input-field) {
  gap: 0px !important;
  margin-top: 0px !important;
  padding-top: 0px !important;
  min-height: auto !important;
}

:deep(.payment-proof-field .input-box .input-field .input-title) {
  display: none !important;
}

/* Force all date picker inputs to neutral state by default */
:deep(.custom-date-picker input[type='text']) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

:deep(.custom-date-picker input[type='text']:focus) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

:deep(.custom-date-picker input[type='text']:hover) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

/* Override any existing focus styles from the CustomDatePicker component */

/* Additional override for any potential CSS conflicts */
:deep(.custom-date-picker) {
  position: relative;
}

:deep(.custom-date-picker .input-text) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

/* Specific targeting for individual pickers to prevent highlighting */
:deep(.payment-due-picker .input-text) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

:deep(.extension-picker .input-text) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

/* Only allow highlighting for the billing month picker when it's active */
:deep(.billing-month-picker .input-text) {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

/* Nuclear option: Override ALL input focus styles in the modal */
.modal-container input:focus {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

.modal-container input:hover {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}

.modal-container input {
  background: #f2f2f2 !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  color: #2b2a2a !important;
}
</style>
